/* ============================================================================
   muc-ciudades.js · Mi Universo Cuántico · buscador de ~90.000 lugares (compartido home + PDP)
   Origen: web-src/pdp/pdp2.js L402-438 y home v7 L2239-2301 (v7.6, Q-41). Datos: GeoNames (CC BY 4.0).
   Un IIFE sin dependencias; cuelga de window.MUC.ciudades. No lleva ninguna URL escrita.

   ARCHIVO. Un .json (o .json.gz) subido a Contenido > Archivos, generado con web-src/v6/build_geo.py:
     {"t":[zonas], "a":[provincias], "c":[países ISO], "f":"nombre|alt|a|c|lat|lon|t\n..."}  (ya ordenado por población).
   Se descarga al enfocar un campo enlazado, o en reposo, tras la primera interacción, cuando el formulario (`[data-ciudades-url]`) está a menos de 800 px
   de la pantalla (CRO-1 E6; no con «ahorro de datos»), o si alguien llama a MUC.ciudades.cargar().
   Si empieza por los bytes de gzip se descomprime con DecompressionStream('gzip'); si no, se lee como JSON.
   Así vale tanto un .gz servido tal cual como uno que el CDN ya entregue descomprimido (Content-Encoding).
   URL, por orden: opciones.url · data-ciudades-url del campo o de un contenedor ancestro · MUC.ciudades.configurar({url})
                   · window.MUC_CONFIG.ciudadesUrl. En Liquid: data-ciudades-url="{{ settings.muc_ciudades_url }}".
   Si falla la descarga: se usa el respaldo (opcional, ver configurar) o ninguna sugerencia; reintenta en el siguiente foco.

   API
     MUC.ciudades.enlazar(input, opciones)  -> controlador {valor(), establecer(lugar|texto), limpiar(), cerrar()}
        input   <input type="text" role="combobox" aria-controls="ID_UL" aria-expanded="false" autocomplete="off">
        opciones (todas opcionales):
          lista       <ul> de sugerencias (por defecto el id de aria-controls). Cada <li> sale con role="option",
                      id = ul.id + '-' + i, <b>nombre</b><small>provincia · país</small>; el aviso sale como <li class="nada">.
          url         URL del archivo (ver arriba)
          alElegir    function(lugar, input)  al elegir (clic, Intro o salir del campo con el primer resultado exacto)
          alLimpiar   function(input)         al editar el texto después de haber elegido (el lugar deja de valer)
          validar     true -> input.setCustomValidity(textos.elegir) mientras el texto no sea un lugar elegido (home)
          seleccionar true -> al enfocar con un lugar ya elegido, selecciona el texto (home)
          textos      {cargando, nada, elegir}  o atributos del campo data-texto-cargando / data-texto-nada / data-texto-elegir
                      Mensajes de estado/error con texto por defecto (los únicos textos del módulo).
        Eventos DOM en el input (burbujean): 'muc:ciudad' (detail = lugar) y 'muc:ciudad-limpia'.
        lugar = {lugar:'Valencia, España', nombre, adm, cc:'ES', lat, lon, tz:'Europe/Madrid'}
                (lat/lon/tz son lo que necesita MUC.astro.calcular; lugar es lo que se muestra y va al pedido)
        Teclado: flechas, Intro (elige), Esc (cierra). El foco se queda en el campo (aria-activedescendant).
     MUC.ciudades.configurar({url, respaldo, textos})
        respaldo = [[«Ciudad, País», lat, lon, tz], …] (la lista corta de la home v7 L1639-1652) para cuando no hay descarga.
     MUC.ciudades.cargar(url?)    -> Promise<array>  (idempotente; la fuerza el foco)
     MUC.ciudades.buscar(texto)   -> hasta 8 resultados {n, alt, adm, cc, la, lo, tz, k, k2[, pn]}; admite «ciudad, país o provincia»
     MUC.ciudades.etiqueta(g)     -> 'Valencia, España'
     MUC.ciudades.pais(cc)        -> nombre del país en español (Intl.DisplayNames)
     MUC.ciudades.paisLocal()     -> país de quien compra, por su zona horaria (si no se reconoce, 'ES')
     MUC.ciudades.normalizar(t)   -> sin tildes, minúsculas, solo [a-z0-9ñ ]
   Orden de resultados: hasta 5 ciudades del país de quien compra, luego hasta 3 de otros países, el resto, y por último
   coincidencias de palabra interior («valencia» + Intro ya no da Valencia, Venezuela a quien compra desde España).
   ============================================================================ */
(() => {
  'use strict';
  const MUC = (window.MUC = window.MUC || {});
  const TXT = {cargando: 'Cargando ciudades…', nada: 'No la encontramos. Prueba con el municipio o la ciudad más cercana.', elegir: 'Elige tu lugar de nacimiento de la lista.'};
  const DS = {cargando: 'textoCargando', nada: 'textoNada', elegir: 'textoElegir'};
  const cfg = {url: '', respaldo: null, textos: {}};
  let GEO = null, cargando = null, fallo = false, miCC = '';

  const normT = t => String(t).normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9ñ ]+/g, ' ').replace(/\s+/g, ' ').trim();
  const esc = s => String(s).replace(/[<>&"]/g, '');
  let paisDe = cc => cc;
  try { const dn = new Intl.DisplayNames(['es'], {type: 'region'}); paisDe = cc => { try { return dn.of(cc) || cc; } catch (e) { return cc; } }; } catch (e) {}
  const etiqueta = g => g.n + ', ' + (g.pn || paisDe(g.cc));
  const respaldo = () => { const r = cfg.respaldo || (window.MUC_CONFIG && window.MUC_CONFIG.ciudadesRespaldo) || [];
    return r.map(c => { const [n, p] = String(c[0]).split(', '); return {n, alt: '', adm: '', cc: '', pn: p, la: c[1], lo: c[2], tz: c[3], k: normT(n), k2: ''}; }); };
  /* datos utilizables: los reales, o (tras un fallo) el respaldo; null = todavía no hay nada */
  let RESP = null;
  const datos = () => GEO || (fallo ? (RESP || (RESP = respaldo())) : null);
  const urlDe = el => { const n = el && el.closest ? el.closest('[data-ciudades-url]') : null; return n ? n.dataset.ciudadesUrl : ''; };

  function cargar(url){
    if (GEO) return Promise.resolve(GEO);
    if (cargando) return cargando;
    const u = url || cfg.url || (window.MUC_CONFIG && window.MUC_CONFIG.ciudadesUrl) || '';
    cargando = (async () => {
      if (!u) throw new Error('falta la URL del archivo de ciudades (data-ciudades-url)');
      const r = await fetch(u, {credentials: 'omit'}); if (!r.ok) throw new Error('ciudades: HTTP ' + r.status);
      const b = new Uint8Array(await r.arrayBuffer());
      const txt = b[0] === 0x1f && b[1] === 0x8b ? await new Response(new Blob([b]).stream().pipeThrough(new DecompressionStream('gzip'))).text() : new TextDecoder().decode(b);
      const d = JSON.parse(txt);
      GEO = d.f.split('\n').map(l => { const [n, alt, a, c, la, lo, t] = l.split('|'); return {n, alt, adm: d.a[+a], cc: d.c[+c], la: +la, lo: +lo, tz: d.t[+t], k: normT(n), k2: alt ? normT(alt) : ''}; });
      fallo = false; return GEO;
    })().catch(e => { console.warn('MUC.ciudades', e); fallo = true; cargando = null; return datos() || []; });
    return cargando;
  }

  function paisLocal(){
    const G = GEO; if (miCC || !G || !G.length) return miCC || 'ES';
    let tz = ''; try { tz = Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) {}
    const m = tz && G.find(g => g.tz === tz && g.cc); miCC = m ? m.cc : 'ES'; return miCC;
  }
  function buscar(q){
    let [qn, qp] = String(q).split(','); qn = normT(qn || ''); qp = normT(qp || ''); const G = datos(); if (qn.length < 2 || !G) return [];
    const cc = paisLocal(), L = [], O = [], b = [], pal = ' ' + qn;
    for (let i = 0; i < G.length && L.length < 8; i++){ const g = G[i]; if (qp && !normT((g.pn || paisDe(g.cc)) + ' ' + g.adm).includes(qp)) continue;
      if (g.k.startsWith(qn) || g.k2.startsWith(qn)){ if (g.cc === cc) L.push(g); else if (O.length < 8) O.push(g); }
      else if (b.length < 8 && ((' ' + g.k).includes(pal) || (' ' + g.k2).includes(pal))) b.push(g); }
    return L.slice(0, 5).concat(O.slice(0, 3), L.slice(5), O.slice(3), b).slice(0, 8);
  }

  function enlazar(inp, op){
    op = op || {};
    const ul = op.lista || document.getElementById(inp.getAttribute('aria-controls'));
    const tx = k => (op.textos && op.textos[k]) || inp.dataset[DS[k]] || cfg.textos[k] || TXT[k];
    let res = [], sel = -1, actual = null;
    const cerrar = () => { ul.hidden = true; inp.setAttribute('aria-expanded', 'false'); inp.removeAttribute('aria-activedescendant'); };
    const pintarL = () => {
      ul.innerHTML = res.length ? res.map((g, i) => `<li role="option" id="${ul.id}-${i}" aria-selected="${i === sel}"><b>${esc(g.n)}</b><small>${esc([g.adm, g.pn || paisDe(g.cc)].filter(Boolean).join(' · '))}</small></li>`).join('')
        : '<li class="nada">' + esc(datos() ? tx('nada') : tx('cargando')) + '</li>';
      ul.hidden = false; inp.setAttribute('aria-expanded', 'true');
      if (sel >= 0){ inp.setAttribute('aria-activedescendant', ul.id + '-' + sel); const li = document.getElementById(ul.id + '-' + sel);
        if (li){ const a = li.getBoundingClientRect(), b = ul.getBoundingClientRect(); if (a.top < b.top) ul.scrollTop -= b.top - a.top; else if (a.bottom > b.bottom) ul.scrollTop += a.bottom - b.bottom; } }
    };
    const fijar = (g, texto) => { actual = g; inp.value = texto; inp.dataset.ok = texto; if (op.validar) inp.setCustomValidity(''); };
    const elegir = g => {
      const lugar = {lugar: etiqueta(g), nombre: g.n, adm: g.adm || '', cc: g.cc, lat: g.la, lon: g.lo, tz: g.tz};
      fijar(lugar, lugar.lugar); cerrar();
      inp.dispatchEvent(new CustomEvent('muc:ciudad', {bubbles: true, detail: lugar}));
      op.alElegir && op.alElegir(lugar, inp);
    };
    const actualizar = async () => {
      if (inp.value.trim().length < 2){ cerrar(); return; }
      if (!datos()){ res = []; sel = -1; pintarL(); await cargar(op.url || urlDe(inp)); if (inp.value.trim().length < 2){ cerrar(); return; } }
      res = buscar(inp.value); sel = res.length ? 0 : -1; pintarL();
    };
    inp.addEventListener('focus', () => { cargar(op.url || urlDe(inp)); if (op.seleccionar && inp.value === inp.dataset.ok) inp.select(); });
    inp.addEventListener('input', () => {
      if (op.validar) inp.setCustomValidity(inp.value === inp.dataset.ok ? '' : tx('elegir'));
      if (inp.value !== inp.dataset.ok && actual){ actual = null; inp.dispatchEvent(new CustomEvent('muc:ciudad-limpia', {bubbles: true})); op.alLimpiar && op.alLimpiar(inp); }
      actualizar();
    });
    inp.addEventListener('keydown', e => { if (ul.hidden) return;
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp'){ e.preventDefault(); if (res.length){ sel = (sel + (e.key === 'ArrowDown' ? 1 : -1) + res.length) % res.length; pintarL(); } }
      else if (e.key === 'Enter'){ if (res[sel]){ e.preventDefault(); elegir(res[sel]); } }
      else if (e.key === 'Escape') cerrar(); });
    ul.addEventListener('mousedown', e => { const li = e.target.closest('li[role=option]'); if (!li) return; e.preventDefault(); elegir(res[+li.id.split('-').pop()]); });
    inp.addEventListener('blur', () => setTimeout(() => { cerrar(); if (inp.value !== inp.dataset.ok && res.length && normT(inp.value.split(',')[0]) === res[0].k) elegir(res[0]); }, 120));
    return {
      valor: () => actual,
      establecer: l => { if (l && typeof l === 'object') fijar(l, l.lugar || ''); else fijar(null, String(l || '')); },
      limpiar: () => { actual = null; inp.value = ''; inp.dataset.ok = ''; if (op.validar) inp.setCustomValidity(''); cerrar(); },
      cerrar
    };
  }

  /* CRO-1 E6: precarga en reposo cuando el formulario está cerca; así «Cargando ciudades…» no aparece al enfocar el campo */
  const conexion = navigator.connection || {};
  if ('IntersectionObserver' in window && !conexion.saveData){
    const io = new IntersectionObserver(es => {
      const e = es.find(x => x.isIntersecting); if (!e) return;
      io.disconnect();
      const ir = () => cargar(urlDe(e.target));
      if ('requestIdleCallback' in window) requestIdleCallback(ir, {timeout: 3000}); else setTimeout(ir, 300);
    }, {rootMargin: '800px 0px'});
    /* solo tras la primera interacción (scroll, toque, tecla): descargar y leer ~90.000 lugares no compite con la carga de la página */
    const armar = () => {
      ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach(t => removeEventListener(t, armar, true));
      document.querySelectorAll('[data-ciudades-url]').forEach(n => { if (n.dataset.ciudadesUrl) io.observe(n); });
    };
    ['scroll', 'pointerdown', 'keydown', 'touchstart'].forEach(t => addEventListener(t, armar, {capture: true, passive: true, once: true}));
  }

  MUC.ciudades = {enlazar, cargar, buscar, etiqueta, pais: paisDe, paisLocal, normalizar: normT,
    configurar: o => { o = o || {}; if (o.url) cfg.url = o.url; if (o.respaldo) cfg.respaldo = o.respaldo; if (o.textos) Object.assign(cfg.textos, o.textos); }};
})();
